import React, { useEffect, useMemo, useRef } from 'react'
import { useForm, UseFormSetValue } from 'react-hook-form'
import { useIntl } from 'react-intl'
import NewFilterStore from '../../../stores/NewFilterStore'
import RequestFilterChip from '../../RequestsManagement/components/NewRequestFilters/components/RequestFilterChip'
import { CustomerTableParamsType } from '../types/customer-filters.types'
import {
  customersPaginationStore,
  useCustomerTableData,
} from './useCustomerTableData'

export type CustomerFiltersType = {
  customerId: string
  equipmentId: string
  agreementNumber: string
  phoneNumber: string
  fullName: string
  fullAddress: string
  receiverSerialNumber: string
}

export const defaultValues: CustomerFiltersType = {
  fullAddress: '',
  equipmentId: '',
  fullName: '',
  agreementNumber: '',
  phoneNumber: '',
  customerId: '',
  receiverSerialNumber: '',
}

export const customersFilterStore = new NewFilterStore<CustomerFiltersType>(
  defaultValues,
)

export const useCustomerTableFilters = () => {
  const intl = useIntl()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { handleSubmit, reset, control, setValue } =
    useForm<CustomerFiltersType>({
      defaultValues,
    })

  const { data } = useCustomerTableData()
  const { getParams, clear, setParams, deleteParam, currentFilters } =
    customersFilterStore

  useEffect(() => {
    if (Object.keys(currentFilters).length) {
      fillValues(currentFilters, setValue)
    }

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current)
    }
  }, [])

  const onSearch = (data?: CustomerFiltersType) => {
    data && setParams(data)
    const params = getParams() as Partial<CustomerTableParamsType>
    return Promise.resolve(customersPaginationStore.setParams(params))
  }

  const onSubmit = (data: CustomerFiltersType) => onSearch(data)

  const onReset = () => {
    reset(defaultValues)
    clear()
    onSearch()
  }

  const handleDeleteChip = (propName: keyof CustomerFiltersType) => {
    setValue(propName, defaultValues[propName])

    deleteParam(propName)

    timeoutRef.current && clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      onSearch()
    }, 0)
  }

  const setChip = (params: any) => (
    <RequestFilterChip {...params} onClick={handleDeleteChip} />
  )

  return useMemo(
    () => ({
      setChip,
      intl,
      data,
      onSearch,
      currentFilters,
      handleSubmit,
      onSubmit,
      onReset,
      control,
    }),
    [data, currentFilters],
  )
}

const fillValues = (
  currentFilters: Partial<CustomerFiltersType>,
  setValue: UseFormSetValue<CustomerFiltersType>,
) => {
  Object.keys(currentFilters).forEach((key) =>
    setValue(key as keyof CustomerFiltersType, (currentFilters as any)[key]),
  )
}
